import type { UniversalMediaPlayer } from "universal-media-player";
import {
  VideoContainerName,
  VideoEventNameToBehavior,
  VideoPlayerEvent,
} from "./VideoPlayer.constants";
import type {
  CaptionType,
  ContentTagsType,
  DownloadableType,
  SourcesType,
  VideoCustomEventType,
  VideoOptionsType,
} from "./VideoPlayer.types";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "universal-media-player": UniversalMediaPlayerAttributes;
    }
    interface UniversalMediaPlayerAttributes {
      id: string;
      ref: React.RefObject<UniversalMediaPlayer>;
      options: any;
    }
  }
}

const isPowerCMSVideoId = (videoId: string) =>
  /^[A-Za-z]{2}[A-Za-z0-9]{1,9}$/.test(videoId); // powerCMS aka Red Tiger

const isValidVideoStream = (
  highQualityVideoUrl: string,
  mediumQualityVideoUrl: string,
  lowQualityVideoUrl: string,
  adaptiveVideoDashUrl: string,
  adaptiveVideoHLSUrl: string
) =>
  highQualityVideoUrl.length > 0 ||
  mediumQualityVideoUrl.length > 0 ||
  lowQualityVideoUrl.length > 0 ||
  adaptiveVideoDashUrl.length > 0 ||
  adaptiveVideoHLSUrl.length > 0;

const createPlayerOptions = (
  videoId: string,
  videoTitle: string,
  locale: string,
  autoplay: boolean,
  highQualityVideoUrl: string,
  mediumQualityVideoUrl: string,
  lowQualityVideoUrl: string,
  adaptiveVideoDashUrl: string,
  adaptiveVideoHLSUrl: string,
  poster: string,
  captions?: Array<CaptionType>,
  downloadableFiles?: Array<DownloadableType>
) => {
  const videoOptions: VideoOptionsType = {
    partnerName: "copilotLab",
    title: videoTitle,
    language: locale,
    reporting: false,
    autoplay: autoplay || false,
    poster,
  };
  if (captions && captions.length > 0) {
    videoOptions.ccFiles = captions;
    videoOptions.ccFiles?.forEach((ccFile) => {
      const ccFileExt = ccFile?.url?.split(".").pop();
      if (
        ccFileExt?.toLowerCase() === "vtt" ||
        ccFileExt?.toLocaleLowerCase() === "ttml"
      ) {
        ccFile.ccType = ccFileExt.toUpperCase();
      }
    });
  }

  if (downloadableFiles && downloadableFiles.length > 0) {
    videoOptions.downloadableFiles = downloadableFiles;
  }

  if (
    isValidVideoStream(
      highQualityVideoUrl,
      mediumQualityVideoUrl,
      lowQualityVideoUrl,
      adaptiveVideoDashUrl,
      adaptiveVideoHLSUrl
    )
  ) {
    // learn.microsoft.com video options
    videoOptions.sources = [];

    if (highQualityVideoUrl) {
      videoOptions.sources.push({
        src: highQualityVideoUrl,
        type: "MP4",
        quality: "HQ",
      } as SourcesType);
    }

    if (mediumQualityVideoUrl) {
      videoOptions.sources.push({
        src: mediumQualityVideoUrl,
        type: "MP4",
        quality: "HD",
      } as SourcesType);
    }

    if (lowQualityVideoUrl) {
      videoOptions.sources.push({
        src: lowQualityVideoUrl,
        type: "MP4",
        quality: "LO",
      } as SourcesType);
    }

    if (adaptiveVideoDashUrl) {
      videoOptions.sources.push({
        src: adaptiveVideoDashUrl,
        type: "DASH",
      } as SourcesType);
    }

    if (adaptiveVideoHLSUrl) {
      videoOptions.sources.push({
        src: adaptiveVideoHLSUrl,
        type: "HLS",
      } as SourcesType);
    }
  } else if (isPowerCMSVideoId(videoId)) {
    videoOptions.plugins = {
      powerCms: { videoId },
    };
  }
  return videoOptions;
};

const onVideoEvent = (eventData: VideoCustomEventType) => {
  const {
    id,
    event,
    videoId,
    videoTitle,
    duration,
    currentTime,
    muted,
    currMuteState,
    updateMutedState,
    telemetry,
  } = eventData;

  let eventName;
  switch (event) {
    case VideoPlayerEvent.FullscreenChange:
      eventName = document.fullscreenElement
        ? VideoPlayerEvent.FullscreenEnter
        : VideoPlayerEvent.FullscreenExit;
      break;
    case VideoPlayerEvent.Play:
      eventName =
        currentTime > 0 ? VideoPlayerEvent.Resume : VideoPlayerEvent.Play;
      break;
    case VideoPlayerEvent.TimeUpdate:
    case VideoPlayerEvent.VolumeChange:
      // if mute state changes, we need to fire the correct event
      if (currMuteState !== muted) {
        eventName = muted ? VideoPlayerEvent.Mute : VideoPlayerEvent.Unmute;
        updateMutedState(muted);
      } else {
        eventName = event;
      }
      break;
    default:
      eventName = event;
      break;
  }

  const videoDiv = document.getElementById(id);

  if (
    !eventName ||
    !VideoEventNameToBehavior.has(eventName) ||
    !videoDiv ||
    !videoId ||
    !videoTitle
  ) {
    return;
  }

  //the content tags as they exist today, commented out are properties which cannot be computed currently or whose value is unknown
  const contentTags: ContentTagsType = {
    id: `videoMetaData_${videoId}`,
    vidnm: videoTitle,
    vidid: videoId,
    vidpct: duration > 0 ? Math.round(100 * (currentTime / duration)) : 0,
    vidwt: Math.round(currentTime),
    viddur: Math.round(duration),
    vidtimeseconds: Math.round(duration),
    live: false,
    containerName: VideoContainerName,
  };

  const pageOverride = {
    actionType: "O",
    behavior: VideoEventNameToBehavior.get(eventName),
    content: contentTags,
    isAuto: false,
  };

  telemetry.sendVideoTelemetryEvent(pageOverride);
};

export {
  createPlayerOptions,
  isPowerCMSVideoId,
  isValidVideoStream,
  onVideoEvent,
};
